import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";

const MessageContext = createContext();

export const useMessages = () => {
  const context = useContext(MessageContext);
  if (!context) {
    throw new Error("useMessages must be used within a MessageProvider");
  }
  return context;
};

export const MessageProvider = ({ children }) => {
  // Get current user ID from Redux state to scope storage per user
  const userState = useSelector((state) => state.user);
  const currentUserId = userState?.id || userState?.user_id || "guest";

  // Create user-scoped storage keys
  const storageKeys = useMemo(
    () => ({
      sessions: `aiChatSessions_${currentUserId}`,
      currentSession: `currentAISessionId_${currentUserId}`,
    }),
    [currentUserId]
  );

  // Session management
  const [currentSessionId, setCurrentSessionId] = useState(() => {
    const saved = sessionStorage.getItem(storageKeys.currentSession);
    return saved || uuidv4();
  });

  // Messages storage - persisted in session storage (cleared when browser tab closes)
  const [sessions, setSessions] = useState(() => {
    const saved = sessionStorage.getItem(storageKeys.sessions);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (error) {
        console.error("Error parsing saved sessions:", error);
      }
    }
    return {};
  });

  // Get messages for current session
  const currentMessages = sessions[currentSessionId] || [];

  // Debug logging (can be removed in production)
  // console.log("MessageContext Debug:", { currentSessionId, currentMessages: currentMessages.length });

  // Save to sessionStorage whenever sessions change (with user-scoped keys)
  useEffect(() => {
    sessionStorage.setItem(storageKeys.sessions, JSON.stringify(sessions));
    sessionStorage.setItem(storageKeys.currentSession, currentSessionId);
  }, [sessions, currentSessionId, storageKeys]);

  // Clear sessions and create new session when user changes
  useEffect(() => {
    const saved = sessionStorage.getItem(storageKeys.sessions);
    if (saved) {
      try {
        setSessions(JSON.parse(saved));
      } catch (error) {
        console.error("Error parsing saved sessions:", error);
        setSessions({});
      }
    } else {
      setSessions({});
    }

    const savedSessionId = sessionStorage.getItem(storageKeys.currentSession);
    setCurrentSessionId(savedSessionId || uuidv4());
  }, [currentUserId, storageKeys]);

  // Add message to current session
  const addMessage = useCallback(
    (message) => {
      const messageWithId = {
        ...message,
        id: message.id || Date.now() + Math.random(),
        timestamp: message.timestamp || new Date().toISOString(),
        sessionId: currentSessionId,
      };

      setSessions((prev) => ({
        ...prev,
        [currentSessionId]: [...(prev[currentSessionId] || []), messageWithId],
      }));

      return messageWithId;
    },
    [currentSessionId]
  );

  // Update existing message (for streaming responses)
  const updateMessage = useCallback(
    (messageId, updates) => {
      setSessions((prev) => ({
        ...prev,
        [currentSessionId]: (prev[currentSessionId] || []).map((msg) =>
          msg.id === messageId ? { ...msg, ...updates } : msg
        ),
      }));
    },
    [currentSessionId]
  );

  // Clear current session messages
  const clearCurrentSession = useCallback(() => {
    setSessions((prev) => ({
      ...prev,
      [currentSessionId]: [],
    }));
  }, [currentSessionId]);

  // Start new session
  const startNewSession = useCallback(() => {
    const newSessionId = uuidv4();
    setCurrentSessionId(newSessionId);
    return newSessionId;
  }, []);

  // Switch to existing session
  const switchToSession = useCallback((sessionId) => {
    setCurrentSessionId(sessionId);
  }, []);

  // Get all session IDs with message counts
  const getAllSessions = useCallback(() => {
    return Object.keys(sessions)
      .map((sessionId) => ({
        id: sessionId,
        messageCount: sessions[sessionId]?.length || 0,
        lastMessage: sessions[sessionId]?.slice(-1)[0]?.timestamp || null,
        title: `Chat ${Object.keys(sessions).indexOf(sessionId) + 1}`,
      }))
      .sort((a, b) => new Date(b.lastMessage || 0) - new Date(a.lastMessage || 0));
  }, [sessions]);

  // Clear all sessions (for logout/reset)
  const clearAllSessions = useCallback(() => {
    setSessions({});
    setCurrentSessionId(uuidv4());
    sessionStorage.removeItem(storageKeys.sessions);
    sessionStorage.removeItem(storageKeys.currentSession);
  }, [storageKeys]);

  // Check if message should be truncated for compact view
  const shouldTruncateMessage = useCallback((message, isCompact = false) => {
    if (!isCompact) return false;

    // Check text content length
    if (message.content && message.content.length > 200) return true;

    // Check if has response items (charts/complex content)
    if (message.responseItems && message.responseItems.length > 0) {
      // Check for charts or long text items
      return message.responseItems.some((item) => item.type !== "text" || (item.text && item.text.length > 200));
    }

    return false;
  }, []);

  // Truncate message content for compact view
  const getTruncatedMessage = useCallback((message, maxLength = 200) => {
    const truncated = { ...message };

    if (message.content && message.content.length > maxLength) {
      truncated.content = message.content.substring(0, maxLength) + "...";
      truncated.isTruncated = true;
    }

    if (message.responseItems && message.responseItems.length > 0) {
      truncated.responseItems = message.responseItems.map((item) => {
        if (item.type === "text" && item.text && item.text.length > maxLength) {
          return {
            ...item,
            text: item.text.substring(0, maxLength) + "...",
            isTruncated: true,
          };
        }
        // For charts, mark as truncated but keep structure
        if (item.type !== "text") {
          return { ...item, isTruncated: true };
        }
        return item;
      });
      truncated.isTruncated = true;
    }

    return truncated;
  }, []);

  const contextValue = {
    // Session management
    currentSessionId,
    currentMessages,
    sessions,

    // Session operations
    startNewSession,
    switchToSession,
    clearCurrentSession,
    clearAllSessions,
    getAllSessions,

    // Message operations
    addMessage,
    updateMessage,

    // Truncation utilities
    shouldTruncateMessage,
    getTruncatedMessage,
  };

  return <MessageContext.Provider value={contextValue}>{children}</MessageContext.Provider>;
};

export default MessageContext;
